import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/toast/ToastProvider';
import styles from '@/pages/ProjectsPage.module.css';

type Props = {
  audienceId: string;
  integrationId: string | null;
};

export function AudienceBrevoPanel({ audienceId, integrationId }: Props) {
  const qc = useQueryClient();
  const toast = useToast();

  const linkQ = useQuery({
    queryKey: ['brevo-audience', integrationId, audienceId],
    enabled: Boolean(integrationId && audienceId),
    queryFn: () =>
      apiFetch<{
        link: {
          brevo_list_id: number;
          contact_count: number;
          last_sync_at: string | null;
          last_error: string | null;
        } | null;
      }>(`/api/integrations/${integrationId}/brevo/audiences/${audienceId}`),
  });

  const sync = useMutation({
    mutationFn: () =>
      apiFetch<{ brevo_list_id: number; contact_count: number }>(
        `/api/integrations/${integrationId}/brevo/sync-audience`,
        {
          method: 'POST',
          body: JSON.stringify({ audience_id: audienceId }),
        },
      ),
    onSuccess: (data) => {
      toast.push(`Brevo sync: ${data.contact_count} contacten`, 'success');
      void qc.invalidateQueries({ queryKey: ['brevo-audience', integrationId, audienceId] });
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  if (!integrationId) {
    return (
      <section className={styles.card}>
        <h3>Brevo</h3>
        <p className={styles.mutedSmall}>Koppel eerst Brevo onder Integraties.</p>
      </section>
    );
  }

  const link = linkQ.data?.link;

  return (
    <section className={styles.card}>
      <h3>Brevo list sync</h3>
      <p className={styles.mutedSmall}>
        Synchroniseert verzendbare leads (geen DNC/suppressies) naar een Brevo-lijst voor marketing
        campagnes.
      </p>
      {link ? (
        <dl className={styles.mutedSmall}>
          <dt>Brevo list ID</dt>
          <dd>{link.brevo_list_id}</dd>
          <dt>Contacten</dt>
          <dd>{link.contact_count}</dd>
          {link.last_sync_at ? (
            <>
              <dt>Laatste sync</dt>
              <dd>{new Date(link.last_sync_at).toLocaleString('nl-NL')}</dd>
            </>
          ) : null}
        </dl>
      ) : null}
      {link?.last_error ? <p className={styles.mutedSmall}>{link.last_error}</p> : null}
      <Button variant="secondary" disabled={sync.isPending} onClick={() => sync.mutate()}>
        {sync.isPending ? 'Sync…' : 'Sync naar Brevo'}
      </Button>
    </section>
  );
}
